import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useVoucherContext } from '../../context/vouchers/VoucherContext';
import ItineraryTableView from './sections/ItineraryTable/ItineraryTableView';
import MainLayout from '../../Layout/MainLayout';
import PageAnimator from '../../components/layout/PageAnimator';
import FullItinerarySkeleton from './sections/ItineraryTable/FullItinerarySkeleton';

const FullItineraryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getVoucherById, voucherService } = useVoucherContext();

  const [loading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState(null);
  const [formData, setFormData] = useState({
    itinerary: [],
    contact_details: [],
    important_documents: []
  });
  const [formVoucherTheme, setFormVoucherTheme] = useState({
    include_itinerary: true,
    isCustom: false,
    primaryColor: '#067978'
  });
  const [isEditingItinerary, setIsEditingItinerary] = useState(false);

  const voucherRef = useRef(voucher);
  const formDataRef = useRef(formData);
  const formThemeRef = useRef(formVoucherTheme);

  useEffect(() => {
    voucherRef.current = voucher;
  }, [voucher]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    formThemeRef.current = formVoucherTheme;
  }, [formVoucherTheme]);

  useEffect(() => {
    const loadVoucher = async () => {
      try {
        setLoading(true);
        const data = await getVoucherById(id);
        if (!data) {
          toast.error('Voucher not found.');
          navigate('/voucher');
          return;
        }
        setVoucher(data);
        const initial = {
          ...data,
          itinerary: data.itinerary || [],
          contact_details: data.contact_details || [],
          important_documents: data.important_documents || []
        };
        setFormData(initial);
        if (data.voucher_theme) {
          setFormVoucherTheme(prev => ({
            ...prev,
            ...data.voucher_theme,
            include_itinerary: data.voucher_theme.include_itinerary !== false
          }));
        }
      } catch (err) {
        console.error('Failed to load voucher itinerary:', err);
        toast.error('Failed to load itinerary.');
        navigate('/voucher');
      } finally {
        setLoading(false);
      }
    };

    loadVoucher();
  }, [id, getVoucherById, navigate]);

  // Direct updater for itinerary data (saves directly to backend API)
  const updateItineraryData = useCallback(async (updater, customSuccessMessage = null) => {
    const currentVoucher = voucherRef.current;
    if (!currentVoucher) return;

    const prevData = formDataRef.current;
    const nextFormData = typeof updater === 'function' ? updater(prevData) : updater;

    setFormData(nextFormData);
    formDataRef.current = nextFormData;

    const sanitizedContactDetails = Array.isArray(nextFormData.contact_details)
      ? nextFormData.contact_details.map(({ id, ...rest }) => rest)
      : (nextFormData.contact_details || []);

    const updatedVoucher = {
      ...currentVoucher,
      itinerary: nextFormData.itinerary,
      contact_details: sanitizedContactDetails,
      important_documents: nextFormData.important_documents !== undefined
        ? nextFormData.important_documents
        : (currentVoucher.important_documents || []),
      voucher_theme: {
        ...currentVoucher.voucher_theme,
        ...formThemeRef.current
      }
    };

    const fd = new FormData();
    fd.append('voucherData', JSON.stringify(updatedVoucher));

    try {
      const saved = await voucherService.updateVoucher(currentVoucher._id, fd, true);
      const resVoucher = saved?.voucher || saved || updatedVoucher;
      setVoucher(resVoucher);
      voucherRef.current = resVoucher;
      if (customSuccessMessage) {
        toast.success(customSuccessMessage);
      }
      return resVoucher;
    } catch (err) {
      console.error('Failed to update itinerary:', err);
      toast.error(err.response?.data?.message || 'Failed to save changes.');
      throw err;
    }
  }, [voucherService]);

  // Local-only updater for keystrokes (e.g. while typing in day titles)
  const setLocalFormData = useCallback((updater) => {
    setFormData(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      formDataRef.current = next;
      return next;
    });
  }, []);

  // Direct updater for voucher theme (e.g. Include in PDF switch)
  const updateVoucherTheme = useCallback(async (updater) => {
    const currentVoucher = voucherRef.current;
    if (!currentVoucher) return;

    const prevTheme = formThemeRef.current;
    const nextTheme = typeof updater === 'function' ? updater(prevTheme) : updater;

    setFormVoucherTheme(nextTheme);
    formThemeRef.current = nextTheme;

    const updatedVoucher = {
      ...currentVoucher,
      voucher_theme: {
        ...currentVoucher.voucher_theme,
        ...nextTheme
      }
    };

    const fd = new FormData();
    fd.append('voucherData', JSON.stringify(updatedVoucher));

    try {
      const saved = await voucherService.updateVoucher(currentVoucher._id, fd, true);
      const resVoucher = saved?.voucher || saved || updatedVoucher;
      setVoucher(resVoucher);
      voucherRef.current = resVoucher;
      toast.success('Voucher PDF settings updated');
      return resVoucher;
    } catch (err) {
      console.error('Failed to update voucher theme:', err);
      toast.error(err.response?.data?.message || 'Failed to update settings');
      throw err;
    }
  }, [voucherService]);

  const addItineraryDay = useCallback(() => {
    updateItineraryData(prev => {
      const currentDays = prev.itinerary || [];
      const newDayIndex = currentDays.length + 1;
      const newDayId = `day_${Date.now()}`;
      const newDay = {
        id: newDayId,
        day_number: newDayIndex,
        day: `Day ${newDayIndex}`,
        title: `Day ${newDayIndex} Sightseeing`,
        day_title: `Day ${newDayIndex} Sightseeing`,
        date: '',
        summary: '',
        events: [
          {
            id: `evt_${newDayIndex}_1`,
            title: 'Morning Activity',
            activity: 'Morning Activity',
            time: '10:00',
            description: ''
          }
        ]
      };
      return {
        ...prev,
        itinerary: [...currentDays, newDay]
      };
    }, 'Added new itinerary day');
  }, [updateItineraryData]);

  const guestName = voucher?.guest_information?.guest_name || 'Guest';
  const bookingId = voucher?.booking_id || voucher?._id || id;

  // Header info passed into ItineraryTableView for inline display
  const headerInfo = {
    bookingId,
    guestName,
    dashboardPath: `/voucher-dashboard/${id}`
  };

  if (loading) {
    return (
      <MainLayout activeTab="voucher">
        <FullItinerarySkeleton />
      </MainLayout>
    );
  }

  return (
    <MainLayout activeTab="voucher">
      <PageAnimator className="bg-slate-50 min-h-screen text-[#3a4d5e] pb-16 font-sans">
        {/* Main Itinerary Content */}
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6 lg:p-7">
            <ItineraryTableView
              formData={formData}
              setFormData={updateItineraryData}
              setLocalFormData={setLocalFormData}
              formVoucherTheme={formVoucherTheme}
              setFormVoucherTheme={updateVoucherTheme}
              addItineraryDay={addItineraryDay}
              headerInfo={headerInfo}
              isEditingItinerary={isEditingItinerary}
              setIsEditingItinerary={setIsEditingItinerary}
            />
          </div>
        </div>
      </PageAnimator>
    </MainLayout>
  );
};

export default FullItineraryPage;
